import { extractedVatNeedsReview, isSameVatNumber } from '@shared/validation/partita-iva';
import { useProfile } from '../pages/profile/hooks/use-profile-queries';

export type SupplierVatHint = 'format-error' | 'needs-review' | 'own-company' | 'default';

const VAT_HINT_MESSAGE_KEYS: Record<SupplierVatHint, string> = {
  'format-error': 'suppliers:vatNumberInvalid',
  'needs-review': 'goodsReceiving:vatNumberNeedsReview',
  'own-company': 'common:vatNumberOwnCompany',
  default: 'suppliers:vatNumberHint',
};

interface UseSupplierVatHintInput {
  vatNumber: string | undefined;
  hasFormatError: boolean;
}

export interface SupplierVatHintResult {
  hint: SupplierVatHint;
  /** Namespace-qualified i18n key, valid from any `useTranslation` namespace. */
  messageKey: string;
  /** Warning (yellow) hints inform without blocking the save. */
  isWarning: boolean;
}

function resolveSupplierVatHint(
  { vatNumber, hasFormatError }: UseSupplierVatHintInput,
  tenantVatNumber: string | null | undefined,
): SupplierVatHint {
  if (hasFormatError) {
    return 'format-error';
  }
  if (isSameVatNumber(vatNumber, tenantVatNumber)) {
    return 'own-company';
  }
  if (extractedVatNeedsReview(vatNumber)) {
    return 'needs-review';
  }
  return 'default';
}

/**
 * Hint under the supplier P.IVA field. Informs, never blocks: only the
 * resolver's format error is an error. Own company wins over needs-review
 * because it names the actual mistake (the tenant's VAT in the supplier field).
 */
export function useSupplierVatHint(input: UseSupplierVatHintInput): SupplierVatHintResult {
  // auth/profile is open to every member; tenant-settings needs SETTINGS and
  // would hide the own-company warning from operators.
  const { data: profile } = useProfile();
  const hint = resolveSupplierVatHint(input, profile?.tenant?.partitaIva);
  return {
    hint,
    messageKey: VAT_HINT_MESSAGE_KEYS[hint],
    isWarning: hint === 'needs-review' || hint === 'own-company',
  };
}
